/**
 * Coda durevole delle scritture eseguite senza rete (PostgREST: insert / update / delete).
 *
 * - Ogni voce nasce già con l'id della riga (generato sul dispositivo): il reinvio è idempotente.
 * - L'ordine è rispettato (FIFO). Un errore di rete ferma l'invio e riprova più tardi; un rifiuto del
 *   server (permessi, vincoli) segna la voce «non accettata» e va rivista dall'utente (mai persa in silenzio).
 * - La coda alimenta anche una «sovrapposizione» sulle letture: le righe create/modificate offline si
 *   vedono subito nelle liste, finché non vengono sincronizzate.
 */
import { idbAll, idbDelete, idbPut } from "./idb";
import { violaIndiceNumeroDdt } from "./conflittiUnicita";

/** UUID v4 (con ripiego per iOS < 15.4, dove crypto.randomUUID non esiste) */
export function uuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export type WriteOp = "insert" | "update" | "delete";

export interface QueuedWrite {
  id: string;
  /** utente che ha eseguito la modifica: viene inviata solo da quello stesso account */
  uid: string | null;
  seq: number;
  table: string;
  method: "POST" | "PATCH" | "DELETE";
  /** tabella + query (relativo a /rest/v1/) */
  path: string;
  headers: Record<string, string>;
  body: string | null;
  op: WriteOp;
  rowIds: string[];
  rows?: Record<string, unknown>[];
  patch?: Record<string, unknown>;
  createdAt: number;
  status: "pending" | "failed";
  error?: string;
  attempts: number;
}

let queue: QueuedWrite[] = [];
let loaded: Promise<void> | null = null;
let seqCounter = 0;
let flushing = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* ignora */
    }
  });
}

export function ensureQueueLoaded(): Promise<void> {
  if (!loaded) {
    loaded = idbAll<QueuedWrite>("queue")
      .then((rows) => {
        queue = rows.sort((a, b) => a.seq - b.seq);
        seqCounter = queue.reduce((m, r) => Math.max(m, r.seq), 0);
        emit();
      })
      .catch(() => undefined);
  }
  return loaded;
}

export const getQueue = (): QueuedWrite[] => queue;
export const pendingCount = (): number => queue.filter((q) => q.status === "pending").length;
export const failedCount = (): number => queue.filter((q) => q.status === "failed").length;
export const pendingForTable = (table: string): QueuedWrite[] =>
  queue.filter((q) => q.status === "pending" && q.table === table);

export function subscribeQueue(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function enqueue(w: Omit<QueuedWrite, "id" | "seq" | "createdAt" | "status" | "attempts">): Promise<QueuedWrite> {
  await ensureQueueLoaded();
  const item: QueuedWrite = {
    ...w,
    id: uuid(),
    seq: ++seqCounter,
    createdAt: Date.now(),
    status: "pending",
    attempts: 0,
  };
  queue.push(item);
  await idbPut("queue", item as unknown as Record<string, unknown>);
  emit();
  return item;
}

export async function discardWrite(id: string): Promise<void> {
  queue = queue.filter((q) => q.id !== id);
  await idbDelete("queue", id);
  emit();
}

export async function retryFailed(): Promise<void> {
  for (const q of queue) {
    if (q.status === "failed") {
      q.status = "pending";
      q.error = undefined;
      await idbPut("queue", q as unknown as Record<string, unknown>);
    }
  }
  emit();
}

export async function clearQueue(): Promise<void> {
  const ids = queue.map((q) => q.id);
  queue = [];
  await Promise.all(ids.map((id) => idbDelete("queue", id)));
  emit();
}

interface FlushDeps {
  /** utente attualmente collegato: le voci di altri utenti restano in attesa */
  currentUid: () => Promise<string | null>;
  restBase: string;
  apiKey: string;
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  isNetworkError: (e: unknown) => boolean;
}

export interface FlushResult {
  sent: number;
  failed: number;
  remaining: number;
  /** interrotto per mancanza di rete */
  paused: boolean;
}

/** Invia in ordine le voci in attesa. Sicuro da chiamare più volte (una sola esecuzione alla volta). */
export async function flushQueue(deps: FlushDeps): Promise<FlushResult> {
  await ensureQueueLoaded();
  if (flushing) return { sent: 0, failed: 0, remaining: pendingCount(), paused: false };
  flushing = true;
  let sent = 0;
  let failed = 0;
  let paused = false;
  try {
    const uid = await deps.currentUid();
    for (const item of [...queue].sort((a, b) => a.seq - b.seq)) {
      if (item.status !== "pending") continue;
      if (item.uid && uid && item.uid !== uid) continue;
      let token = await deps.getToken(false);
      if (!token) {
        paused = true;
        break;
      }
      const headersFor = (tk: string | null): Record<string, string> => ({
        ...item.headers,
        apikey: deps.apiKey,
        Authorization: `Bearer ${tk ?? ""}`,
      });
      /** Tutte le righe dell'inserimento esistono già sul server (con lo stesso id)? */
      const righeGiaPresenti = async (w: QueuedWrite, h: Record<string, string>): Promise<boolean> => {
        if (!w.rowIds.length) return false;
        try {
          const lettura: Record<string, string> = { apikey: h.apikey, Authorization: h.Authorization, accept: "application/json" };
          if (w.headers["content-profile"]) lettura["accept-profile"] = w.headers["content-profile"];
          const ids = w.rowIds.map((x) => `"${x}"`).join(",");
          const r = await fetch(`${deps.restBase}${w.table}?select=id&id=in.(${encodeURIComponent(ids)})`, { headers: lettura });
          if (!r.ok) return false;
          const rows = (await r.json()) as { id?: string }[];
          const trovati = new Set(rows.map((x) => String(x.id)));
          return w.rowIds.every((x) => trovati.has(x));
        } catch {
          return false;
        }
      };
      const doSend = async (tk: string) => {
        const headers: Record<string, string> = headersFor(tk);
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 20_000);
        try {
          return await fetch(`${deps.restBase}${item.path}`, {
            method: item.method,
            headers,
            body: item.body ?? undefined,
            signal: ctrl.signal,
          });
        } finally {
          clearTimeout(t);
        }
      };
      try {
        let res = await doSend(token);
        if (res.status === 401) {
          token = await deps.getToken(true);
          if (!token) {
            paused = true;
            break;
          }
          res = await doSend(token);
        }
        if (res.status >= 500) {
          paused = true;
          break;
        }
        let ok = res.ok;
        let message = "";
        if (!ok) {
          const text = await res.text().catch(() => "");
          try {
            const j = JSON.parse(text);
            message = String(j?.message ?? j?.error ?? text).slice(0, 300);
            // riga già presente (stesso id): l'invio precedente era arrivato
            if (res.status === 409 && item.op === "insert" && /pkey|duplicate key/i.test(`${j?.message ?? ""} ${j?.details ?? ""}`)) {
              // Eccezione solo per il numero DDT (F4.1): doppione vero, salvo righe già presenti sul server.
              // Ogni altro vincolo unico resta «già inviato» come sempre.
              ok = violaIndiceNumeroDdt(String(j?.message ?? "")) ? await righeGiaPresenti(item, headersFor(token)) : true;
            }
          } catch {
            message = text.slice(0, 300) || `Errore ${res.status}`;
          }
          if (!ok && !message) message = `Errore ${res.status}`;
        }
        if (ok) {
          sent++;
          queue = queue.filter((q) => q.id !== item.id);
          await idbDelete("queue", item.id);
        } else {
          failed++;
          item.status = "failed";
          item.error = message;
          item.attempts += 1;
          await idbPut("queue", item as unknown as Record<string, unknown>);
        }
        emit();
      } catch (e) {
        if (deps.isNetworkError(e)) {
          paused = true;
          break;
        }
        failed++;
        item.status = "failed";
        item.error = e instanceof Error ? e.message : String(e);
        await idbPut("queue", item as unknown as Record<string, unknown>);
        emit();
      }
    }
  } finally {
    flushing = false;
  }
  return { sent, failed, remaining: pendingCount(), paused };
}
